import React, { useEffect, useRef, useState, useCallback } from 'react';
import { trackServerEvent } from '../utils/analytics';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Play, Pause, SkipBack, SkipForward, Download, Share2, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { BlogcastSeo } from '../components/BlogcastSeo';
import { SpotifyIcon } from '../components/SpotifyIcon';
import { setMediaSession } from '../components/BlogcastAudioPlayer';
import { getBlogcastShow } from '../data/blogcastShows';

const API = process.env.REACT_APP_BACKEND_URL;
const pad = (n) => String(n).padStart(2, '0');
const fmt = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const shareEpisode = async (e, show, ep) => {
  e.stopPropagation();
  const url = `${window.location.origin}/blogcast/${show}?ep=${ep.episode_number}`;
  const title = `${ep.title} | The BWGELA Blogcast`;
  trackServerEvent('share', { contentType: 'blogcast', title: ep.title });
  if (navigator.share) {
    try { await navigator.share({ title, text: title, url }); } catch (err) { /* cancelled */ }
  } else {
    try { await navigator.clipboard.writeText(url); toast.success('Link copied'); } catch (err) { /* ignore */ }
  }
};

const BlogcastShow = () => {
  const { show } = useParams();
  const [searchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([
      fetch(`${API}/api/blogcast/shows`).then((r) => r.json()),
      fetch(`${API}/api/blogcast/episodes?category=${show}`).then((r) => r.json()),
    ])
      .then(([s, e]) => {
        setMeta((s.shows || []).find((x) => x.slug === show) || null);
        const eps = e.episodes || [];
        setEpisodes(eps);
        const idx = eps.findIndex((x) => String(x.episode_number) === searchParams.get('ep'));
        if (idx >= 0) setCurrent(idx);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [show]);

  const playIndex = useCallback((i) => setCurrent(i), []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || current === null || !episodes[current]) return;
    a.src = `${API}${episodes[current].audio_url}`;
    const sh = getBlogcastShow(show);
    setMediaSession({ title: episodes[current].title, show: sh?.title, artwork: sh?.cover });
    a.play().catch(() => {});
  }, [current, episodes]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onMeta = () => setDur(a.duration);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
    };
  }, [episodes, current]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  };
  const seek = (e) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Number(e.target.value);
    setCur(Number(e.target.value));
  };
  const step = (delta) => {
    if (current === null) return;
    const next = current + delta;
    if (next >= 0 && next < episodes.length) setCurrent(next);
  };

  const activeEp = current !== null ? episodes[current] : null;
  const maxEp = episodes.reduce((m, e) => Math.max(m, e.episode_number), 0);
  const newestFirst = episodes.length > 1 && episodes[0].episode_number > episodes[episodes.length - 1].episode_number;
  const flipOrder = () => {
    setEpisodes((eps) => [...eps].reverse());
    setCurrent((c) => (c === null ? null : episodes.length - 1 - c));
  };

  return (
    <div className="bg-[#0a0a0a] pt-[120px] sm:pt-[140px] lg:pt-[160px] pb-40 px-4 sm:px-6 lg:px-8" data-testid="blogcast-show-page">
      <BlogcastSeo slug={show} episodes={episodes} />
      <div className="max-w-3xl mx-auto">
        <Link to="/blogcast" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-8" data-testid="blogcast-back">
          <ArrowLeft className="h-4 w-4" /> All series
        </Link>

        {meta && (
          <div className="flex flex-col sm:flex-row gap-6 items-start mb-10">
            {meta.cover && (
              <img src={meta.cover} alt={meta.title} className="w-36 h-36 sm:w-44 sm:h-44 rounded-2xl object-cover border border-[#2a2a2a] shrink-0" />
            )}
            <div>
              <h1 className="text-3xl md:text-4xl text-[#F5F5DC] mb-3">{meta.title}</h1>
              <p className="text-base text-white/80 leading-relaxed">{meta.description}</p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <p className="text-gray-500 text-sm">{episodes.length} episode{episodes.length !== 1 ? 's' : ''} · tap a title to listen</p>
                {episodes.length > 1 && (
                  <button
                    onClick={flipOrder}
                    data-testid="blogcast-sort-toggle"
                    aria-label="Reverse episode order"
                    className="inline-flex items-center gap-1.5 text-sm text-[#F5F5DC] border border-[#333] hover:border-[#F5F5DC]/60 rounded-full px-3 py-1 transition-colors"
                  >
                    <ArrowUpDown className="h-3.5 w-3.5" />
                    {newestFirst ? 'Newest first' : 'Oldest first'}
                  </button>
                )}
              </div>
              {meta.spotify_url && (
                <a
                  href={meta.spotify_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="blogcast-show-spotify"
                  className="inline-flex items-center gap-2 mt-4 rounded-full bg-[#1DB954] text-black text-sm font-semibold px-4 py-2 shadow hover:bg-[#1ed760] transition-colors"
                >
                  <SpotifyIcon className="w-4 h-4" /> Listen on Spotify
                </a>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-gray-500 text-sm">Loading episodes…</p>
        ) : (
          <div className="space-y-2" data-testid="blogcast-episode-list">
            {episodes.map((ep, i) => {
              const isActive = current === i;
              return (
                <div
                  key={ep.id}
                  onClick={() => (isActive ? toggle() : playIndex(i))}
                  data-testid={`blogcast-episode-${ep.episode_number}`}
                  className={`group flex items-center gap-3 rounded-lg px-3 py-3 border transition-all cursor-pointer ${isActive ? 'bg-[#1d1b16] border-[#F5F5DC]' : 'bg-[#1a1a1a] border-[#333] hover:border-[#444]'}`}
                >
                  <span className="w-10 h-10 rounded-full bg-[#F5F5DC] text-black flex items-center justify-center shrink-0" data-testid={`episode-play-${ep.episode_number}`}>
                    {isActive && playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                  </span>
                  <span className="text-[#F5F5DC]/40 text-xs tabular-nums shrink-0 w-6">{pad(ep.episode_number)}</span>
                  <h3 className="text-[#F5F5DC] text-sm sm:text-base flex-1 min-w-0 whitespace-normal break-words sm:truncate group-hover:text-white transition-colors">{ep.title}</h3>
                  {ep.episode_number === maxEp && ep.published_at && Date.now() - new Date(ep.published_at).getTime() < 7 * 864e5 && (
                    <span data-testid={`episode-new-${ep.episode_number}`} className="shrink-0 text-[10px] uppercase tracking-wider bg-[#F5F5DC] text-black rounded-full px-2 py-0.5">New</span>
                  )}
                  <span className="text-gray-500 text-xs shrink-0 hidden sm:inline tabular-nums">{fmt(ep.duration)}</span>
                  <div className="flex flex-col sm:flex-row items-center shrink-0">
                    <a
                      href={`${API}${ep.audio_url}`}
                      download
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Download episode"
                      data-testid={`episode-download-${ep.episode_number}`}
                      className="text-gray-500 hover:text-[#F5F5DC] transition-colors p-1.5"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                    <button
                      onClick={(e) => shareEpisode(e, show, ep)}
                      aria-label="Share episode"
                      data-testid={`episode-share-${ep.episode_number}`}
                      className="text-gray-500 hover:text-[#F5F5DC] transition-colors p-1.5"
                    >
                      <Share2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sticky player */}
      <audio ref={audioRef} preload="metadata" />
      {activeEp && (
        <div className="fixed bottom-0 inset-x-0 z-50 bg-[#F5F5DC] border-t border-black/10 shadow-[0_-8px_30px_rgba(0,0,0,0.4)]" data-testid="blogcast-sticky-player">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3 sm:gap-4">
            {meta?.cover && <img src={meta.cover} alt="" className="w-11 h-11 rounded-md object-cover shrink-0 hidden sm:block" />}
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => step(-1)} disabled={current === 0} aria-label="Previous" data-testid="player-prev" className="w-9 h-9 rounded-full text-black flex items-center justify-center disabled:opacity-30 hover:bg-black/10 transition-colors">
                <SkipBack className="h-4 w-4" />
              </button>
              <button onClick={toggle} aria-label={playing ? 'Pause' : 'Play'} data-testid="player-toggle" className="w-11 h-11 rounded-full bg-black text-[#F5F5DC] flex items-center justify-center hover:bg-black/80 transition-colors">
                {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
              </button>
              <button onClick={() => step(1)} disabled={current === episodes.length - 1} aria-label="Next" data-testid="player-next" className="w-9 h-9 rounded-full text-black flex items-center justify-center disabled:opacity-30 hover:bg-black/10 transition-colors">
                <SkipForward className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-black text-xs sm:text-sm font-medium truncate" data-testid="player-title">{pad(activeEp.episode_number)} · {activeEp.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <input type="range" min="0" max={dur || 0} step="0.1" value={cur} onChange={seek} data-testid="player-seek" className="flex-1 h-1 accent-black cursor-pointer" />
                <span className="text-black/60 text-[11px] tabular-nums whitespace-nowrap">{fmt(cur)} / {fmt(dur)}</span>
              </div>
            </div>
            <a href={`${API}${activeEp.audio_url}`} download aria-label="Download" data-testid="player-download" className="text-black/60 hover:text-black transition-colors shrink-0 hidden sm:block p-2">
              <Download className="h-4 w-4" />
            </a>
            <button onClick={(e) => shareEpisode(e, show, activeEp)} aria-label="Share" data-testid="player-share" className="text-black/60 hover:text-black transition-colors shrink-0 p-2 -ml-2 sm:-ml-3">
              <Share2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogcastShow;
